import { useNavigate } from "react-router-dom"
import { IconButton, Paper, Typography } from "@mui/material"
import { Close } from "@mui/icons-material"
import { makeStyles } from "tss-react/mui"
import { UnreachableCaseError } from "../../../lib/lang"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { useAppDispatch, useAppSelector } from "../../store/storeHooks"
import { Merchant } from "../../supplier/reporting/merchants/merchantTypes"
import { selectActiveContext } from "../contextSelectors"
import { popContext } from "../contextSlice"
import { Supplier } from "../../supplierTypes/supplierTypes"
import { Tenant } from "../../tenants/tenantTypes"
import { Company } from "../../companies/companyTypes"
import { ContextUser } from "../contextTypes"
import { ServiceCenter } from "../../serviceCenter/serviceCenterTypes"

const useStyles = makeStyles()((theme) => ({
    wrapper: {
        position: "relative",
        display: "flex",
        alignItems: "center",
    },
    display: {
        position: "relative",
        padding: theme.spacing(1, 2),
        color: theme.palette.common.white,
        backgroundColor: theme.palette.common.black,
    },
    closeButton: {
        position: "absolute",
        right: 0,
        bottom: 0,
        padding: "2px",
        // Icon inside is 1rem and we have 2px padding in each side
        marginRight: "calc(-1 * (2px + 2px + 1rem) / 2)",
        // Only move the button quarter out vertically to conserve space
        marginBottom: "calc(-1 * (2px + 2px + 1rem) / 4)",
        fontSize: "1rem",
        color: theme.palette.tailwind.slate[50],
        backgroundColor: theme.palette.tailwind.sky[500],
        "&:hover": {
            backgroundColor: theme.palette.tailwind.sky[600],
        },
    },
}))

type OrgUnitDisplayProps = Readonly<{
    label: string
    identifier?: string
    name: string
    returnUrl: string
}>

function OrgUnitDisplay({ label, identifier, name, returnUrl }: OrgUnitDisplayProps) {
    const { classes } = useStyles()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    function handleCloseContext() {
        dispatch(popContext())
        navigate(returnUrl)
    }

    return (
        <div className={classes.wrapper}>
            <Paper elevation={0} className={classes.display}>
                <Typography variant="caption">
                    <span>{label} </span>
                    {identifier && <span>{identifier}, </span>}
                    <span>{name}</span>
                </Typography>
                <IconButton onClick={handleCloseContext} className={classes.closeButton}>
                    <Close fontSize="inherit" />
                </IconButton>
            </Paper>
        </div>
    )
}

type SupplierDisplayProps = Readonly<{
    supplier: Supplier
    returnUrl: string
}>

function SupplierDisplay({ supplier, returnUrl }: SupplierDisplayProps) {
    return (
        <LocalizedStrict id="context-display-org-unit-supplier" attrs={{ label: true }}>
            <OrgUnitDisplay label="Supplier:" name={supplier.name} returnUrl={returnUrl} />
        </LocalizedStrict>
    )
}

type MerchantDisplayProps = Readonly<{
    merchant: Merchant
    returnUrl: string
}>

function MerchantDisplay({ merchant, returnUrl }: MerchantDisplayProps) {
    return (
        <LocalizedStrict id="context-display-org-unit-merchant" attrs={{ label: true }}>
            <OrgUnitDisplay
                label="Merchant:"
                identifier={merchant.caseNumber}
                name={merchant.name}
                returnUrl={returnUrl}
            />
        </LocalizedStrict>
    )
}

type TenantDisplayProps = Readonly<{
    tenant: Tenant
    returnUrl: string
}>

function TenantDisplay({ tenant, returnUrl }: TenantDisplayProps) {
    return (
        <LocalizedStrict id="context-display-org-unit-tenant" attrs={{ label: true }}>
            <OrgUnitDisplay label="Tenant:" name={tenant.name} returnUrl={returnUrl} />
        </LocalizedStrict>
    )
}

type CompanyDisplayProps = Readonly<{
    company: Company
    returnUrl: string
}>

function CompanyDisplay({ company, returnUrl }: CompanyDisplayProps) {
    return (
        <LocalizedStrict id="context-display-org-unit-company" attrs={{ label: true }}>
            <OrgUnitDisplay label="Company:" name={company.name} returnUrl={returnUrl} />
        </LocalizedStrict>
    )
}

type UserDisplayProps = Readonly<{
    user: ContextUser
    returnUrl: string
}>

function UserDisplay({ user, returnUrl }: UserDisplayProps) {
    return (
        <LocalizedStrict id="context-display-org-unit-user" attrs={{ label: true }}>
            <OrgUnitDisplay label="User:" name={user.displayName} returnUrl={returnUrl} />
        </LocalizedStrict>
    )
}

type ServiceCenterDisplayProps = Readonly<{
    serviceCenter: ServiceCenter
    returnUrl: string
}>

function ServiceCenterDisplay({ serviceCenter, returnUrl }: ServiceCenterDisplayProps) {
    return (
        <LocalizedStrict id="context-display-org-unit-service-center" attrs={{ label: true }}>
            <OrgUnitDisplay label="Serivce center:" name={serviceCenter.name} returnUrl={returnUrl} />
        </LocalizedStrict>
    )
}

type ContextDisplayProps = Readonly<{}>

export function ContextDisplay({}: ContextDisplayProps) {
    const context = useAppSelector(selectActiveContext)

    if (!context) return <div></div>

    switch (context.type) {
        case "supplier":
            return <SupplierDisplay supplier={context.supplier} returnUrl={context.returnUrl} />
        case "merchant":
            return <MerchantDisplay merchant={context.merchant} returnUrl={context.returnUrl} />
        case "tenant":
            return <TenantDisplay tenant={context.tenant} returnUrl={context.returnUrl} />
        case "company":
            return <CompanyDisplay company={context.company} returnUrl={context.returnUrl} />
        case "user":
            return <UserDisplay user={context.user} returnUrl={context.returnUrl} />
        case "serviceCenter":
            return <ServiceCenterDisplay serviceCenter={context.serviceCenter} returnUrl={context.returnUrl} />
        default:
            throw new UnreachableCaseError(context)
    }
}
